import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Truck, Calculator, CurrencyDollar, MapPin, Clock,
  Sparkle, CheckCircle, PencilSimple, Play, Pause,
} from "@phosphor-icons/react";
import { trackConversion } from "../../lib/analytics";

/*  Interactive Workflow Preview  ─────────────────────────────────────────
    A live 3-step demo that lets a visitor see FreightPilot AI turn a raw
    broker load into a full margin breakdown and driver payroll in ≈12s.

    - Auto-plays on view; pauses if the user edits any field.
    - All math is deterministic and does NOT hit the backend — this is a
      marketing preview, not a real calculation engine.
    - Ends with the primary "Get One Workflow Automated Free" CTA.
   ────────────────────────────────────────────────────────────────────── */

const SAMPLE_LOAD = {
  pickup: "Chicago, IL",
  delivery: "Dallas, TX",
  miles: 950,
  rate: 2800,
  equipment: "Dry Van · 45,000 lbs",
};

const DIESEL_PRICE = 4.05;   // per gallon
const MPG = 6.5;
const DRIVER_RATE = 0.55;    // per mile
const MAINTENANCE_RATE = 0.15;
const FACTORING_PCT = 0.03;
const DISPATCH_PCT = 0.05;

function fmt(n) {
  return `$${Math.round(n).toLocaleString()}`;
}

function useAutoAdvance(step, setStep, paused, maxStep = 2) {
  const timerRef = useRef(null);
  useEffect(() => {
    if (paused) return;
    if (step >= maxStep) return;
    const delay = step === 0 ? 3800 : 4500;
    timerRef.current = setTimeout(() => setStep((s) => Math.min(maxStep, s + 1)), delay);
    return () => clearTimeout(timerRef.current);
  }, [step, paused, setStep, maxStep]);
}

export default function WorkflowPreviewSection() {
  const [load, setLoad] = useState(SAMPLE_LOAD);
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);
  const [edited, setEdited] = useState(false);
  const [thinking, setThinking] = useState(false);

  // When entering steps 1 or 2, simulate a brief "AI thinking" state.
  useEffect(() => {
    if (step === 1 || step === 2) {
      setThinking(true);
      const t = setTimeout(() => setThinking(false), 900);
      return () => clearTimeout(t);
    }
  }, [step]);

  useAutoAdvance(step, setStep, paused);

  const totals = useMemo(() => {
    const miles = Math.max(1, Number(load.miles) || 0);
    const rate = Math.max(0, Number(load.rate) || 0);
    const fuel = (miles / MPG) * DIESEL_PRICE;
    const driver = miles * DRIVER_RATE;
    const maintenance = miles * MAINTENANCE_RATE;
    const factoring = rate * FACTORING_PCT;
    const dispatch = rate * DISPATCH_PCT;
    const totalCost = fuel + driver + maintenance + factoring + dispatch;
    const margin = rate - totalCost;
    const marginPct = rate > 0 ? (margin / rate) * 100 : 0;
    const rpm = miles > 0 ? rate / miles : 0;
    return { miles, rate, fuel, driver, maintenance, factoring, dispatch, totalCost, margin, marginPct, rpm };
  }, [load]);

  // Weekly payroll snapshot for the driver (2 prior loads + the current one).
  const payroll = useMemo(() => {
    const prior = [
      { route: "Kansas City → Chicago", miles: 510, pay: 510 * DRIVER_RATE },
      { route: "Chicago → Milwaukee", miles: 92, pay: 92 * DRIVER_RATE },
    ];
    const currentLoad = { route: `${load.pickup} → ${load.delivery}`, miles: totals.miles, pay: totals.driver };
    const loads = [...prior, currentLoad];
    const totalMiles = loads.reduce((a, b) => a + b.miles, 0);
    const gross = loads.reduce((a, b) => a + b.pay, 0);
    const fuelAdvance = 285;
    const taxes = gross * 0.153;   // SE tax approx for 1099
    const net = gross - fuelAdvance - taxes;
    return { loads, totalMiles, gross, fuelAdvance, taxes, net };
  }, [load, totals]);

  const onEdit = (patch) => {
    setEdited(true);
    setPaused(true);
    setLoad((l) => ({ ...l, ...patch }));
    setStep(0);
  };

  const restart = () => {
    setLoad(SAMPLE_LOAD);
    setStep(0);
    setPaused(false);
    setEdited(false);
  };

  const jumpTo = (i) => {
    setPaused(true);
    setStep(i);
  };

  return (
    <section
      id="workflow-preview"
      data-testid="workflow-preview-section"
      className="relative border-b border-white/10 py-20 sm:py-28 overflow-hidden scroll-mt-20"
    >
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-50"
        style={{
          background:
            "radial-gradient(45% 40% at 50% 30%, rgba(255,90,31,0.10) 0%, rgba(255,90,31,0) 70%)",
        }}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <div className="max-w-3xl mb-10 sm:mb-14">
          <div className="overline mb-3 text-[#FF9764] inline-flex items-center gap-2">
            <Sparkle size={12} weight="fill" /> Live demo — 12 seconds
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]">
            One load in.{" "}
            <span className="text-[#FF5A1F]">Full margin + payroll out.</span>
          </h2>
          <p className="text-neutral-400 mt-4 text-sm sm:text-base leading-relaxed">
            Watch what your dispatcher does in 20 minutes happen automatically. The numbers below
            are live — <button onClick={() => jumpTo(0)} className="underline decoration-[#FF5A1F]/60 hover:text-white">edit the load</button> and everything recalculates.
          </p>
        </div>

        {/* ── Stepper header ── */}
        <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs">
            {["Add load", "AI margin breakdown", "Driver payroll"].map((label, i) => {
              const active = step === i;
              const done = step > i;
              return (
                <button
                  key={label}
                  onClick={() => jumpTo(i)}
                  data-testid={`workflow-step-tab-${i}`}
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 border transition-colors ${
                    active
                      ? "border-[#FF5A1F] bg-[#FF5A1F]/10 text-white"
                      : done
                      ? "border-emerald-400/40 bg-emerald-400/5 text-emerald-300 hover:text-white"
                      : "border-white/10 bg-white/[0.02] text-neutral-400 hover:text-white hover:border-white/25"
                  }`}
                >
                  <span
                    className={`h-5 w-5 rounded-full text-[10px] font-bold inline-flex items-center justify-center ${
                      active
                        ? "bg-[#FF5A1F] text-black"
                        : done
                        ? "bg-emerald-400 text-black"
                        : "bg-white/10 text-neutral-300"
                    }`}
                  >
                    {done ? "✓" : i + 1}
                  </span>
                  {label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPaused((p) => !p)}
              data-testid="workflow-play-toggle"
              className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5"
            >
              {paused ? <Play size={12} weight="fill" /> : <Pause size={12} weight="fill" />}
              {paused ? "Play" : "Pause"}
            </button>
            {edited && (
              <button
                onClick={restart}
                data-testid="workflow-reset"
                className="text-xs text-neutral-400 hover:text-white rounded-full border border-white/10 px-3 py-1.5"
              >
                Reset demo
              </button>
            )}
          </div>
        </div>

        {/* ── Main widget canvas ── */}
        <div className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-sm overflow-hidden shadow-[0_0_60px_-20px_rgba(255,90,31,0.35)]">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr]">
            {/* LEFT: load form (persistent) */}
            <div className="p-6 sm:p-7 border-b lg:border-b-0 lg:border-r border-white/10 bg-white/[0.015]">
              <div className="flex items-center justify-between mb-5">
                <div className="inline-flex items-center gap-2 text-neutral-200 font-semibold text-sm">
                  <Truck size={16} weight="bold" className="text-[#FF5A1F]" />
                  Broker load offered
                </div>
                <span className="text-[10px] uppercase tracking-widest text-neutral-500">
                  Editable
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <LoadField
                  label="Pickup"
                  icon={<MapPin size={12} weight="bold" className="text-[#FF9764]" />}
                  value={load.pickup}
                  onChange={(v) => onEdit({ pickup: v })}
                  testid="workflow-input-pickup"
                />
                <LoadField
                  label="Delivery"
                  icon={<MapPin size={12} weight="bold" className="text-[#FF9764]" />}
                  value={load.delivery}
                  onChange={(v) => onEdit({ delivery: v })}
                  testid="workflow-input-delivery"
                />
                <LoadField
                  label="Miles"
                  value={load.miles}
                  onChange={(v) => onEdit({ miles: Number(v.replace(/[^0-9]/g, "")) || 0 })}
                  testid="workflow-input-miles"
                />
                <LoadField
                  label="Broker offer"
                  prefix="$"
                  value={load.rate}
                  onChange={(v) => onEdit({ rate: Number(v.replace(/[^0-9]/g, "")) || 0 })}
                  testid="workflow-input-rate"
                />
              </div>

              <div className="mt-4 flex items-center gap-2 text-xs text-neutral-500">
                <Clock size={12} weight="bold" />
                <span>{load.equipment}</span>
                <span className="ml-auto text-neutral-400 font-mono">
                  {totals.rpm.toFixed(2)} $/mi
                </span>
              </div>

              {step === 0 && (
                <div className="mt-6 rounded-xl border border-dashed border-[#FF5A1F]/30 bg-[#FF5A1F]/[0.05] p-4 text-xs text-neutral-300 flex items-start gap-2">
                  <PencilSimple size={14} weight="bold" className="text-[#FF5A1F] shrink-0 mt-0.5" />
                  <span>
                    <span className="text-white font-medium">Try it:</span> change the miles or rate — the AI recalculates margin & payroll instantly.
                  </span>
                </div>
              )}
            </div>

            {/* RIGHT: the animated result panel */}
            <div className="p-6 sm:p-7 min-h-[380px] relative">
              {step === 0 && <StepIntro onNext={() => jumpTo(1)} />}
              {step === 1 && (
                <StepBreakdown
                  totals={totals}
                  thinking={thinking}
                  onNext={() => jumpTo(2)}
                />
              )}
              {step === 2 && <StepPayroll payroll={payroll} thinking={thinking} />}
            </div>
          </div>

          {/* ── Bottom CTA bar ── */}
          <div className="border-t border-white/10 bg-gradient-to-r from-[#FF5A1F]/[0.08] via-transparent to-[#FF5A1F]/[0.06] px-6 sm:px-8 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="text-sm">
              <div className="font-medium text-white">
                That took ~12 seconds.{" "}
                <span className="text-neutral-400 font-normal">Your dispatcher does it in 20 minutes.</span>
              </div>
              <div className="text-xs text-neutral-500 mt-1">
                Your first workflow set up live with Michael — included in the 7-day trial.
              </div>
            </div>
            <Link
              to="/contact?intent=free-workflow"
              onClick={() => trackConversion("workflow_preview_cta")}
              data-testid="workflow-preview-cta"
              className="btn-primary rounded-full px-5 py-3 text-sm inline-flex items-center gap-2 whitespace-nowrap"
            >
              Get One Workflow Automated Free
              <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── Sub-components ─────────── */

function LoadField({ label, value, onChange, testid, icon, prefix }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 flex items-center gap-1.5">
        {icon} {label}
      </span>
      <div className="flex items-center rounded-lg border border-white/10 bg-black/40 focus-within:border-[#FF5A1F]/50 transition-colors">
        {prefix && <span className="pl-2.5 text-neutral-500 text-sm">{prefix}</span>}
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="bg-transparent px-2.5 py-2 text-sm text-white focus:outline-none w-full"
        />
      </div>
    </label>
  );
}

function StepIntro({ onNext }) {
  return (
    <div className="h-full flex flex-col justify-center" data-testid="workflow-step-intro">
      <div className="overline mb-3 text-[#FF9764]">Step 1 · Load received</div>
      <h3 className="font-display text-2xl sm:text-3xl font-semibold leading-tight mb-3">
        Your dispatcher usually replies to the broker <br className="hidden sm:block" /> in 15–20 minutes.
      </h3>
      <p className="text-neutral-400 text-sm leading-relaxed max-w-md mb-6">
        FreightPilot reads the load, checks broker credit + FMCSA, runs the margin math,
        and hands your dispatcher a go/no-go with a suggested counter — before the coffee cools.
      </p>
      <button
        onClick={onNext}
        data-testid="workflow-run-ai"
        className="self-start btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2"
      >
        <Sparkle size={14} weight="fill" /> Run the AI
        <ArrowRight size={14} weight="bold" />
      </button>
    </div>
  );
}

function StepBreakdown({ totals, thinking, onNext }) {
  return (
    <div className="h-full flex flex-col" data-testid="workflow-step-breakdown">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline text-[#FF9764]">Step 2 · AI margin breakdown</div>
          <h3 className="font-display text-xl sm:text-2xl font-semibold leading-tight">
            <Calculator size={20} weight="bold" className="inline text-[#FF5A1F] mr-2" />
            Live margin analysis
          </h3>
        </div>
        {thinking ? (
          <ThinkingPill />
        ) : (
          <span className="text-[10px] uppercase tracking-widest text-emerald-300 inline-flex items-center gap-1 border border-emerald-400/30 bg-emerald-400/5 px-2 py-1 rounded-full">
            <CheckCircle size={11} weight="fill" /> Ready
          </span>
        )}
      </div>

      <ul className="space-y-1.5 mb-4 font-mono text-[13px]">
        <BreakdownRow label="Broker rate" value={fmt(totals.rate)} positive />
        <BreakdownRow label={`Fuel  · ${totals.miles} mi ÷ ${MPG} mpg × $${DIESEL_PRICE}`} value={`− ${fmt(totals.fuel)}`} />
        <BreakdownRow label={`Driver pay  · $${DRIVER_RATE}/mi`} value={`− ${fmt(totals.driver)}`} />
        <BreakdownRow label={`Maintenance  · $${MAINTENANCE_RATE}/mi`} value={`− ${fmt(totals.maintenance)}`} />
        <BreakdownRow label="Factoring 3%" value={`− ${fmt(totals.factoring)}`} />
        <BreakdownRow label="Dispatch 5%" value={`− ${fmt(totals.dispatch)}`} />
      </ul>

      <div className="rounded-xl border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 p-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#FF9764] mb-0.5">Net margin</div>
          <div className={`font-display text-2xl sm:text-3xl font-semibold ${totals.margin < 0 ? "text-red-300" : "text-white"}`}>
            {fmt(totals.margin)}
            <span className="text-xs text-neutral-400 font-normal ml-2">
              ({totals.marginPct.toFixed(1)}%)
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">AI verdict</div>
          <div className={`text-sm font-semibold ${
            totals.marginPct >= 15 ? "text-emerald-300" : totals.marginPct >= 5 ? "text-yellow-300" : "text-red-300"
          }`}>
            {totals.marginPct >= 15
              ? "Accept — solid load"
              : totals.marginPct >= 5
              ? `Counter at ${fmt(totals.rate + 250)}`
              : "Decline — margin too thin"}
          </div>
        </div>
      </div>

      <button
        onClick={onNext}
        data-testid="workflow-run-payroll"
        className="mt-4 self-start text-xs text-[#FF9764] hover:text-white inline-flex items-center gap-1.5"
      >
        See how this hits driver payroll <ArrowRight size={12} weight="bold" />
      </button>
    </div>
  );
}

function StepPayroll({ payroll, thinking }) {
  return (
    <div className="h-full flex flex-col" data-testid="workflow-step-payroll">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline text-[#FF9764]">Step 3 · Driver payroll</div>
          <h3 className="font-display text-xl sm:text-2xl font-semibold leading-tight">
            <CurrencyDollar size={20} weight="bold" className="inline text-[#FF5A1F] mr-2" />
            This week — driver Alex M.
          </h3>
        </div>
        {thinking ? (
          <ThinkingPill />
        ) : (
          <span className="text-[10px] uppercase tracking-widest text-emerald-300 inline-flex items-center gap-1 border border-emerald-400/30 bg-emerald-400/5 px-2 py-1 rounded-full">
            <CheckCircle size={11} weight="fill" /> Auto-calculated
          </span>
        )}
      </div>

      <ul className="space-y-1.5 mb-4 font-mono text-[13px]">
        {payroll.loads.map((l, i) => (
          <li key={i} className="flex items-baseline justify-between border-b border-white/5 pb-1.5">
            <span className="text-neutral-300 truncate pr-3">
              {l.route}
              <span className="text-neutral-600 ml-2">· {l.miles} mi</span>
            </span>
            <span className="text-emerald-300">{fmt(l.pay)}</span>
          </li>
        ))}
        <li className="flex items-baseline justify-between pt-2">
          <span className="text-neutral-300">Gross ({payroll.totalMiles} mi)</span>
          <span className="text-white font-semibold">{fmt(payroll.gross)}</span>
        </li>
        <li className="flex items-baseline justify-between">
          <span className="text-neutral-500">Fuel advance</span>
          <span className="text-neutral-400">− {fmt(payroll.fuelAdvance)}</span>
        </li>
        <li className="flex items-baseline justify-between">
          <span className="text-neutral-500">Taxes withheld (est.)</span>
          <span className="text-neutral-400">− {fmt(payroll.taxes)}</span>
        </li>
      </ul>

      <div className="mt-auto rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-0.5">Net take-home</div>
          <div className="font-display text-2xl sm:text-3xl font-semibold text-white">
            {fmt(payroll.net)}
          </div>
        </div>
        <div className="text-right text-xs text-neutral-400">
          Direct-deposit ACH triggered<br />
          <span className="text-neutral-500">Friday 6:00 AM</span>
        </div>
      </div>
    </div>
  );
}

function BreakdownRow({ label, value, positive }) {
  return (
    <li className="flex items-baseline justify-between border-b border-white/5 pb-1.5">
      <span className="text-neutral-400 truncate pr-3">{label}</span>
      <span className={positive ? "text-emerald-300" : "text-neutral-300"}>{value}</span>
    </li>
  );
}

function ThinkingPill() {
  return (
    <span
      data-testid="workflow-thinking"
      className="text-[10px] uppercase tracking-widest text-[#FF9764] inline-flex items-center gap-1 border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-2 py-1 rounded-full"
    >
      <span className="inline-flex gap-0.5">
        <span className="h-1 w-1 rounded-full bg-[#FF9764] animate-pulse" />
        <span className="h-1 w-1 rounded-full bg-[#FF9764] animate-pulse [animation-delay:150ms]" />
        <span className="h-1 w-1 rounded-full bg-[#FF9764] animate-pulse [animation-delay:300ms]" />
      </span>
      AI thinking
    </span>
  );
}
